import React, { useEffect, useState } from 'react';
import { Alert, AlertColor, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  countOrdersAwaitingFulfillment,
  countOrdersPending,
  countSobeysOnSale,
  countSobeysUnderCost,
} from '../api';

const POLL_MS = 60_000;

/** Polls a count endpoint and renders a clickable full-width banner when
 *  the count is positive. Stays hidden on fetch failure — these banners
 *  are a nice-to-have, not load-bearing, so an error toast would be noise. */
const CountBanner = ({
  severity,
  fetchCount,
  label,
  to = '/orders',
}: {
  severity: AlertColor;
  fetchCount: (idToken: string) => Promise<{ count: number }>;
  label: (count: number) => React.ReactNode;
  /** Where the banner links. */
  to?: string;
}) => {
  const { idToken } = useAuth();
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    const fetchOnce = () => {
      fetchCount(idToken)
        .then((d) => {
          if (!cancelled) setCount(d.count);
        })
        .catch(() => {
          // Banner stays hidden on failure.
        });
    };
    fetchOnce();
    const id = window.setInterval(fetchOnce, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [idToken, fetchCount]);

  if (!count || count <= 0) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Alert
        severity={severity}
        icon={false}
        sx={{
          borderRadius: 0,
          justifyContent: 'center',
          '& .MuiAlert-message': { textAlign: 'center', fontWeight: 500 },
        }}
      >
        <Box
          component={RouterLink}
          to={to}
          sx={{ color: 'inherit', textDecoration: 'none' }}
        >
          {label(count)}
        </Box>
      </Alert>
    </Box>
  );
};

const FulfillmentBanner = () => (
  <>
    <CountBanner
      severity="warning"
      fetchCount={countOrdersPending}
      label={(count) => `${count} order${count === 1 ? '' : 's'} pending`}
    />
    <CountBanner
      severity="success"
      fetchCount={countOrdersAwaitingFulfillment}
      label={(count) =>
        `${count} order${count === 1 ? '' : 's'} awaiting fulfillment`
      }
    />
    {/* Sobeys price check. Admin-gated like everything else here; hidden
        until the crawler has produced a non-zero count. */}
    <CountBanner
      severity="error"
      fetchCount={countSobeysUnderCost}
      to="/reports/sobeys-prices?status=under_cost"
      label={(count) =>
        `${count} front-room item${count === 1 ? ' is' : 's are'} cheaper at Sobeys or Superstore than our cost`
      }
    />
    <CountBanner
      severity="info"
      fetchCount={countSobeysOnSale}
      to="/reports/sobeys-prices?status=on_sale"
      label={(count) =>
        `${count} front-room item${count === 1 ? '' : 's'} on sale at Sobeys or Superstore this week`
      }
    />
  </>
);

export default FulfillmentBanner;
